import {Directive, ElementRef, OnDestroy, OnInit} from '@angular/core';
import {ProfileService} from './profile.service';
import {Router} from '@angular/router';

// Helper function to pause execution for a specified time in milliseconds
function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

@Directive({
    selector: 'ion-toolbar',
})
export class ProfileIconDirective implements OnInit, OnDestroy {
    private refreshTimer: any;
    private profileButton: HTMLElement;
    private isConnected: boolean = true;

    constructor(
        private el: ElementRef,
        private profileService: ProfileService,
        private router: Router
    ) {}

    ngOnInit() {
        // Check if the user is logged in and if the toolbar is not inside a modal
        if (this.profileService.isLoggedIn() && !this.isInsideModal()) {
            // Get the email from the profile service
            const email = this.profileService.getEmail();

            // Create the profile button element
            const profileButtons = document.createElement('ion-buttons');
            profileButtons.setAttribute('slot', 'primary');

            // Create the button
            const button = document.createElement('ion-button');

            // Store references for later use
            this.profileButton = button;

            // Create the icon
            const icon = document.createElement('ion-icon');
            icon.setAttribute('name', 'person-circle-outline');
            icon.setAttribute('slot', 'start');

            // Create the label
            const label = document.createElement('ion-label');
            label.textContent = email;

            // Assemble the button
            button.appendChild(icon);
            button.appendChild(label);

            // Add click event listener to the button
            button.addEventListener('click', () => {
                this.navigateToProfile();
            });

            // Assemble the buttons container
            profileButtons.appendChild(button);

            // Append the buttons to the toolbar
            this.el.nativeElement.appendChild(profileButtons);

            // Start the background refresh timer (check every minute)
            this.startBackgroundRefresh();
        }
    }

    ngOnDestroy() {
        // Clear the timer when the directive is destroyed
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
            this.refreshTimer = null;
        }
    }

    navigateToProfile() {
        this.router.navigateByUrl('/profile');
    }

    private startBackgroundRefresh() {
        this.refreshTimer = setInterval(() => {
            this.checkAndRefreshConnection();
        }, 20000);
    }

    private checkAndRefreshConnection() {
        this.performConnectionRefresh();
    }

    private showSkeletonText() {
        if (this.profileButton) {
            this.profileButton.style.opacity = '0.7';
            this.profileButton.style.transition = 'opacity 0.3s ease';
        }
    }

    private hideSkeletonText() {
        if (this.profileButton) {
            this.profileButton.style.opacity = '1';
        }
    }

    private async performConnectionRefresh() {
        // Show skeleton text and hide button during loading
        this.showSkeletonText();

        // Connection successful
        this.isConnected = await this.profileService.isConnected();

        if (this.isConnected) {
            // Call the refresh function
            const needsConnectionRefresh = this.profileService.needsConnectionRefresh();
            const isLoggedIn = this.profileService.isLoggedIn();
            if (needsConnectionRefresh || !isLoggedIn) {
                await this.profileService.connectionRefresh();
            }
            await sleep(1000);
        }
        this.updateButtonState();

        // Hide skeleton text and show button when loading is complete
        this.hideSkeletonText();
    }

    private updateButtonState() {
        if (this.profileButton) {
            if (this.isConnected) {
                // Enable button
                this.profileButton.removeAttribute('disabled');
                this.profileButton.removeAttribute('title');
            } else {
                // Disable button and add tooltip
                this.profileButton.setAttribute('disabled', 'true');
                this.profileButton.setAttribute('title', 'Profile unavailable - No connection');
            }
        }
    }

    // Helper method to check if the toolbar is inside a modal
    private isInsideModal(): boolean {
        let parent = this.el.nativeElement.parentElement;

        // Traverse up the DOM tree to find if any parent is an ion-modal
        while (parent) {
            if (parent.tagName && parent.tagName.toLowerCase() === 'ion-modal') {
                return true;
            }
            parent = parent.parentElement;
        }

        return false;
    }
}
